import type { User } from '@gmn-portal/backend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  type EntityPermissionOverviewEntry,
  payloadClient,
  useEntityPermissionOverview,
  useUserGroups,
  useUsers,
  type Where,
} from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { PaginationNavigation } from '@/shared/components/pagination';
import { TableSearchBar } from '@/shared/components/tables';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import type { AclServiceKey } from '@/shared/utils/permissions';
import type { PermissionCustomerContext, PermissionCustomerContextSource } from '../types';
import { UsersAccessTable } from './users-access-table';

type ViewAsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: AclServiceKey;
  entityId: string;
  customerContexts?: PermissionCustomerContext[];
  selectedUserId?: string | null;
  onSelectUser: (user: User) => void;
};

type UserListSection = {
  id: string;
  title: string;
  users: User[];
  totalPages: number;
  totalItems: number;
  page: number;
  emptyLabel?: string;
  onPageChange: (page: number) => void;
  isLoading: boolean;
};

type ResolvedCustomerContext = {
  id: string;
  customerId: string;
  source: PermissionCustomerContextSource;
};

type CustomerContextUsersPage = {
  docs: User[];
  totalPages: number;
  totalDocs: number;
};

const getCustomerContextId = (context: PermissionCustomerContext) =>
  `customer-${context.customerId}-${context.source}`;

export function ViewAsDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  customerContexts,
  selectedUserId,
  onSelectUser,
}: ViewAsDialogProps) {
  const { t } = useIntl();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [gmnPage, setGmnPage] = useState(1);
  const [customerPagesByContextId, setCustomerPagesByContextId] = useState<Record<string, number>>(
    {}
  );
  const [activeSectionId, setActiveSectionId] = useState<string>('gmn');
  const pageSize = 10;

  const { data: userGroupsData } = useUserGroups({
    limit: 0,
    sort: 'key',
    disabled: !open,
  });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group])),
    [userGroups]
  );

  const gmnGroupIds = useMemo(
    () => userGroups.filter((group) => group.domain === 'gmn').map((group) => group.id),
    [userGroups]
  );

  const customerGroupIds = useMemo(
    () => userGroups.filter((group) => group.domain === 'customer').map((group) => group.id),
    [userGroups]
  );

  const searchFilter = useMemo<Where | null>(() => {
    const value = debouncedSearch.trim();
    if (!value) return null;
    const conditions: Where[] = [
      { email: { like: value } },
      { firstName: { like: value } },
      { lastName: { like: value } },
    ];
    return {
      or: conditions,
    };
  }, [debouncedSearch]);

  const gmnWhere = useMemo(() => {
    if (gmnGroupIds.length === 0) return undefined;
    const and: Where[] = [
      { userGroup: { in: gmnGroupIds } },
      { customer: { exists: false } },
      { archived: { not_equals: true } },
    ];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [gmnGroupIds, searchFilter]);

  const resolvedCustomerContexts = useMemo<ResolvedCustomerContext[]>(() => {
    const contexts = (customerContexts ?? []).filter(
      (context): context is PermissionCustomerContext =>
        typeof context.customerId === 'string' && context.customerId.length > 0
    );
    const mergedByCustomerId = new Map<string, PermissionCustomerContextSource>();
    for (const context of contexts) {
      const existing = mergedByCustomerId.get(context.customerId);
      if (!existing) {
        mergedByCustomerId.set(context.customerId, context.source);
        continue;
      }
      if (existing !== context.source) {
        mergedByCustomerId.set(context.customerId, 'combined');
      }
    }
    return Array.from(mergedByCustomerId.entries()).map(([customerId, source]) => ({
      id: getCustomerContextId({ customerId, source }),
      customerId,
      source,
    }));
  }, [customerContexts]);

  const customerWhereByContextId = useMemo(() => {
    const whereByContextId = new Map<string, Where>();
    for (const context of resolvedCustomerContexts) {
      const and: Where[] = [
        { userGroup: { in: customerGroupIds } },
        { customer: { equals: context.customerId } },
        { archived: { not_equals: true } },
      ];
      if (searchFilter) {
        and.push(searchFilter);
      }
      whereByContextId.set(context.id, { and });
    }
    return whereByContextId;
  }, [customerGroupIds, resolvedCustomerContexts, searchFilter]);

  const customerQueryParams = useMemo(
    () =>
      resolvedCustomerContexts.map((context) => ({
        context,
        page: customerPagesByContextId[context.id] ?? 1,
        where: customerWhereByContextId.get(context.id),
      })),
    [customerPagesByContextId, customerWhereByContextId, resolvedCustomerContexts]
  );

  const {
    data: gmnUsersData,
    isLoading: isLoadingGmnUsers,
    isFetching: isFetchingGmnUsers,
  } = useUsers({
    depth: 0,
    page: gmnPage,
    limit: pageSize,
    sort: 'lastName',
    where: gmnWhere,
    disabled: !open || !gmnWhere,
  });

  const {
    data: customerUsersByContextData,
    isLoading: isLoadingCustomerUsersByContext,
    isFetching: isFetchingCustomerUsersByContext,
  } = useQuery({
    queryKey: ['users', 'view-as-by-customer-context', customerQueryParams, pageSize],
    enabled:
      open &&
      customerGroupIds.length > 0 &&
      customerQueryParams.length > 0 &&
      customerQueryParams.every((param) => Boolean(param.where)),
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      return Promise.all(
        customerQueryParams.map(async (param) => {
          const response = await payloadClient.find({
            collection: 'users',
            depth: 0,
            page: param.page,
            limit: pageSize,
            sort: 'lastName',
            where: param.where,
          });
          return { contextId: param.context.id, response };
        })
      );
    },
  });

  useEffect(() => {
    setGmnPage(1);
    setCustomerPagesByContextId(() => {
      const next: Record<string, number> = {};
      resolvedCustomerContexts.forEach((context) => {
        next[context.id] = 1;
      });
      return next;
    });
  }, [debouncedSearch, resolvedCustomerContexts]);

  useEffect(() => {
    if (gmnUsersData && gmnPage > gmnUsersData.totalPages) {
      setGmnPage(Math.max(1, gmnUsersData.totalPages));
    }
  }, [gmnPage, gmnUsersData]);

  useEffect(() => {
    if (!customerUsersByContextData || customerUsersByContextData.length === 0) return;
    setCustomerPagesByContextId((previous) => {
      let changed = false;
      const next = { ...previous };
      for (const entry of customerUsersByContextData) {
        const currentPage = previous[entry.contextId] ?? 1;
        const totalPages = Math.max(1, entry.response.totalPages ?? 1);
        if (currentPage > totalPages) {
          next[entry.contextId] = totalPages;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [customerUsersByContextData]);

  const customerUsersByContextId = useMemo(() => {
    const map = new Map<string, CustomerContextUsersPage>();
    for (const entry of customerUsersByContextData ?? []) {
      map.set(entry.contextId, {
        docs: (entry.response.docs || []) as User[],
        totalPages: entry.response.totalPages ?? 0,
        totalDocs: entry.response.totalDocs ?? 0,
      });
    }
    return map;
  }, [customerUsersByContextData]);

  const resolveCustomerSectionTitle = (source: PermissionCustomerContextSource) => {
    switch (source) {
      case 'assigned-customer':
        return t('permissions.customerSections.assignedCustomer');
      case 'assigned-helicopter-service-provider':
        return t('permissions.customerSections.helicopterServiceProvider');
      case 'combined':
        return t('permissions.customerSections.combined');
      default:
        return t('permissions.customerUsers');
    }
  };

  const gmnSection: UserListSection = {
    id: 'gmn',
    title: t('permissions.gmnUsers'),
    users: (gmnUsersData?.docs ?? []) as User[],
    totalPages: gmnUsersData?.totalPages ?? 0,
    totalItems: gmnUsersData?.totalDocs ?? 0,
    page: gmnPage,
    onPageChange: setGmnPage,
    isLoading: isLoadingGmnUsers || isFetchingGmnUsers,
  };

  const customerSections: UserListSection[] = resolvedCustomerContexts.map((context) => {
    const response = customerUsersByContextId.get(context.id);
    return {
      id: context.id,
      title: resolveCustomerSectionTitle(context.source),
      users: response?.docs ?? [],
      totalPages: response?.totalPages ?? 0,
      totalItems: response?.totalDocs ?? 0,
      page: customerPagesByContextId[context.id] ?? 1,
      onPageChange: (nextPage) => {
        setCustomerPagesByContextId((previous) => ({ ...previous, [context.id]: nextPage }));
      },
      isLoading: isLoadingCustomerUsersByContext || isFetchingCustomerUsersByContext,
      emptyLabel: t('permissions.noUsersForSection'),
    };
  });

  const sections: UserListSection[] = [gmnSection, ...customerSections];

  useEffect(() => {
    if (sections.length === 0) {
      setActiveSectionId('');
      return;
    }
    if (!sections.some((section) => section.id === activeSectionId)) {
      setActiveSectionId(sections[0].id);
    }
  }, [activeSectionId, sections]);

  const visibleUsers = useMemo(() => {
    const byId = new Map<string, User>();
    for (const section of sections) {
      for (const user of section.users) {
        byId.set(user.id, user);
      }
    }
    return Array.from(byId.values());
  }, [sections]);

  const overviewUserIds = useMemo(() => {
    return visibleUsers.map((user) => user.id);
  }, [visibleUsers]);

  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useEntityPermissionOverview({
    entityType,
    entityId,
    userIds: overviewUserIds,
    disabled: !open || overviewUserIds.length === 0,
  });

  const overviewByUserId = useMemo(() => {
    const map = new Map<string, EntityPermissionOverviewEntry>();
    for (const entry of overviewData?.entries ?? []) {
      map.set(entry.userId, entry);
    }
    return map;
  }, [overviewData?.entries]);

  const hasDisplayUsers = sections.some((section) => section.users.length > 0);
  const isAccessLoading =
    isLoadingOverview ||
    isFetchingOverview ||
    (!hasDisplayUsers && sections.some((section) => section.isLoading));

  const renderTable = (section: UserListSection) => {
    return (
      <div className="space-y-4">
        <UsersAccessTable
          users={section.users}
          userGroupsById={userGroupsById}
          overviewByUserId={overviewByUserId}
          isOverviewLoading={section.isLoading || isAccessLoading}
          emptyLabel={section.emptyLabel}
          renderAction={({ user, readAllowed }) => {
            const isSelected = selectedUserId === user.id;
            return isSelected ? (
              <Badge variant="outline-solid">{t('permissions.viewAs.active')}</Badge>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`view-as-select-${user.id}`}
                onClick={() => {
                  onSelectUser(user);
                }}
                disabled={readAllowed !== true}
              >
                {t('permissions.viewAs.select')}
              </Button>
            );
          }}
        />
        {section.totalPages > 1 ? (
          <PaginationNavigation
            currentPage={section.page}
            totalPages={section.totalPages}
            pageSize={pageSize}
            totalItems={section.totalItems}
            onPageChange={section.onPageChange}
            isLoading={section.isLoading}
          />
        ) : null}
      </div>
    );
  };

  const hasAnyUsers = sections.some((section) => section.totalItems > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] !max-w-[90rem]">
        <DialogHeader>
          <DialogTitle>{t('permissions.viewAs.title')}</DialogTitle>
          <DialogDescription>{t('permissions.viewAs.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col md:flex-row gap-3">
            <TableSearchBar
              searchTerm={search}
              onSearchChange={setSearch}
              placeholder={t('permissions.viewAs.searchPlaceholder')}
            />
          </div>

          {sections.length > 0 ? (
            <Tabs value={activeSectionId} onValueChange={setActiveSectionId}>
              <TabsList className="h-auto bg-transparent p-0 gap-2 border-b rounded-none">
                {sections.map((section) => (
                  <TabsTrigger
                    key={section.id}
                    value={section.id}
                    className="rounded-b-none data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
                    data-testid={`view-as-user-section-${section.id}`}
                  >
                    {section.title}
                  </TabsTrigger>
                ))}
              </TabsList>
              {sections.map((section) => (
                <TabsContent key={section.id} value={section.id} className="m-0 pt-4">
                  {renderTable(section)}
                </TabsContent>
              ))}
            </Tabs>
          ) : null}

          {!isAccessLoading && !hasAnyUsers ? (
            <div className="text-sm text-muted-foreground">{t('permissions.viewAs.noUsers')}</div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
